import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Users, Wallet, TreePine, CreditCard } from 'lucide-react';
import SEOMetadata from '@/components/SEOMetadata';
import StructuredDataFAQ from '@/components/seo/StructuredDataFAQ';
import { webPageStructuredData } from '@/data/structuredData';

// Import FAQ data
import { coachingFAQData } from '@/data/faq/coaching-faq';
import { consultingFAQData } from '@/data/faq/consulting-faq';
import { finanzcoachingFAQData } from '@/data/faq/finanzcoaching-faq';
import { outdoorCoachingFAQData } from '@/data/faq/outdoor-coaching-faq';
import { preiseFAQData } from '@/data/faq/preise-faq';

const FAQ = () => {
  // Combine all FAQ data for structured data
  const allFAQData = [
    ...consultingFAQData,
    ...finanzcoachingFAQData,
    ...outdoorCoachingFAQData,
    ...preiseFAQData
  ];

  const faqCategories = [
    {
      title: "Finanzcoaching",
      description: "Persönliche Entwicklung im Umgang mit Geld und finanzielle Selbstbestimmung",
      icon: Wallet,
      link: "/finanzcoaching#faq",
      color: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100",
      iconColor: "text-emerald-600",
      count: finanzcoachingFAQData.length
    },
    {
      title: "Executive Coaching",
      description: "Führungskräfte-Entwicklung und Katalysator-Führung für Unternehmen",
      icon: Users,
      link: "/consulting#faq",
      color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
      iconColor: "text-purple-600",
      count: consultingFAQData.length
    },
    {
      title: "Outdoor-Coaching",
      description: "Walk & Talk Coaching in der Natur am Rothsee bei Nürnberg",
      icon: TreePine,
      link: "/outdoor-coaching#faq",
      color: "bg-green-50 border-green-200 hover:bg-green-100",
      iconColor: "text-green-600",
      count: outdoorCoachingFAQData.length
    },
    {
      title: "Kosten & Abrechnung",
      description: "Preise, Zahlungskonditionen und Abrechnungsmodalitäten für alle Coaching-Angebote",
      icon: CreditCard,
      link: "/preise#faq",
      color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
      iconColor: "text-blue-600",
      count: preiseFAQData.length
    }
  ];

  return (
    <>
      <SEOMetadata
        metaTitle="FAQ – Häufige Fragen zu Coaching | Jörg Hörmann"
        metaDescription="Antworten auf häufige Fragen zu Finanzcoaching, Executive Coaching und Outdoor-Coaching. Ablauf, Kosten und Methoden verständlich erklärt."
        title="FAQ – Häufige Fragen zu Coaching | Jörg Hörmann"
        description="Antworten auf häufige Fragen zu Finanzcoaching, Executive Coaching und Outdoor-Coaching. Ablauf, Kosten und Methoden verständlich erklärt."
        canonicalUrl="https://joerg-hoermann.de/faq"
        pageName="FAQ"
        primaryLink="/faq"
        imageUrl="/images/og-portrait.jpg"
      />
      
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "FAQ – Häufige Fragen zu Coaching & Finanzcoaching",
            description: "Alle häufigen Fragen zu Finanzcoaching, Executive Coaching und Outdoor-Coaching im Überblick.",
            url: "https://joerg-hoermann.de/faq"
          }))}
        </script>
      </Helmet>

      <StructuredDataFAQ faqItems={allFAQData} pageName="FAQ-Übersicht" />
      
      <main className="min-h-screen">
        {/* AI-Summary / AEO Abstract */}
        <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
          <p>Häufig gestellte Fragen zu Coaching-Angeboten von Jörg Hörmann: Ablauf, Kosten und Methoden zu Finanzcoaching, Executive Coaching und Outdoor-Coaching am Rothsee – verständlich erklärt mit konkreten Antworten.</p>
        </section>

        {/* Hero Section */}
        <section className="section-spacing bg-gradient-to-br from-joerg-50 to-white">
          <div className="container">
            <div className="text-center max-w-4xl mx-auto">
              <span className="inline-block text-primary font-medium mb-6 px-4 py-2 bg-primary/10 rounded-full text-sm animate-fade-in">
                FAQ Übersicht
              </span>
              <h1 className="animate-fade-in mb-6" style={{ animationDelay: '100ms' }}>
                FAQ Übersicht - Alle häufigen Fragen im Überblick
              </h1>
              <p className="text-joerg-600 text-xl leading-relaxed animate-fade-in mb-8" style={{ animationDelay: '200ms' }}>
                Finden Sie schnell Antworten zu Finanzcoaching, Executive Coaching, Outdoor-Coaching und allen Fragen zu Kosten & Abrechnung. 
                Alle wichtigen Informationen zu meinen Coaching-Angeboten auf einen Blick.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="section-spacing bg-white">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {faqCategories.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <Link
                    key={index}
                    to={category.link}
                    className={`${category.color} border-2 rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group animate-fade-in`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className={`${category.iconColor} bg-white p-3 rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-joerg-800 mb-2 group-hover:text-primary transition-colors">
                          {category.title}
                        </h3>
                        <span className="text-sm text-joerg-500 font-medium">
                          {category.count} Fragen beantwortet
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-joerg-600 leading-relaxed mb-6">
                      {category.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all duration-300">
                      <span>FAQ ansehen</span>
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Additional Information */}
        <section className="section-spacing bg-joerg-50">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="mb-6">
                Ihre Frage ist nicht dabei?
              </h2>
              <p className="text-joerg-600 text-lg leading-relaxed mb-8">
                Falls Sie in meinem FAQ-Bereichen nicht die passende Antwort gefunden haben, 
                kontaktieren Sie mich gerne direkt. Ich beantworte Ihre Fragen persönlich und 
                unverbindlich.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:scale-105"
                >
                  Persönlich kontaktieren
                  <ArrowRight className="h-5 w-5" />
                </Link>
                
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-joerg-50 text-primary border-2 border-primary px-8 py-4 rounded-full font-medium transition-all duration-300 hover:scale-105"
                >
                  Kostenloses Erstgespräch
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default FAQ;